/**
 * Maps CASA/SAMS scallop data to the standard fisheryData format
 * used by KeyInsights and other fishery components.
 */

export interface ScallopFisheryDataRow {
  year: number;
  stock_id: number | null;
  stock_name: string;
  mean_ex_vessel: number;
  ex_vessel_uncertainty: number;
  landed_economic_impact: number;
  landed_economic_impact_uncertainty: number;
  missedEconomyValue: number;
  missedEconomyUncertainty: number;
  mean_uncaught_ex_vessel: number;
  uncaught_ex_vessel_uncertainty: number;
  meals_produced: number;
  meals_uncertainty: number;
  trade_deficit_contribution: number;
  trade_deficit_uncertainty: number;
  total_jobs_mean: number;
  total_jobs_uncertainty: number;
  harvesting_jobs_mean: number;
  processing_jobs_mean: number;
  distribution_jobs_mean: number;
  support_services_jobs_mean: number;
  induced_jobs_mean: number;
  mean_uncaught_acl: number;
  uncaught_acl_uncertainty: number;
}

interface YearEconomics {
  total_ex_vessel?: number;
  total_jobs?: number;
  total_meals?: number;
  harvesting_jobs?: number;
  processing_jobs?: number;
  distribution_jobs?: number;
  support_services_jobs?: number;
  induced_jobs?: number;
}

interface AggregatedSamsRow {
  year: number;
  total_abundance?: number;
  total_biomass_mt?: number;
  exploitable_biomass_mt?: number;
}

interface CasaRow {
  year: number;
}

const EMPTY_ROW = (year: number): ScallopFisheryDataRow => ({
  year,
  stock_id: null,
  stock_name: 'Atlantic Scallop',
  mean_ex_vessel: 0,
  ex_vessel_uncertainty: 0,
  landed_economic_impact: 0,
  landed_economic_impact_uncertainty: 0,
  missedEconomyValue: 0,
  missedEconomyUncertainty: 0,
  mean_uncaught_ex_vessel: 0,
  uncaught_ex_vessel_uncertainty: 0,
  meals_produced: 0,
  meals_uncertainty: 0,
  trade_deficit_contribution: 0,
  trade_deficit_uncertainty: 0,
  total_jobs_mean: 0,
  total_jobs_uncertainty: 0,
  harvesting_jobs_mean: 0,
  processing_jobs_mean: 0,
  distribution_jobs_mean: 0,
  support_services_jobs_mean: 0,
  induced_jobs_mean: 0,
  mean_uncaught_acl: 0,
  uncaught_acl_uncertainty: 0,
});

/** Map CASA historical rows (no economics) to fisheryData format. */
export function mapCasaToFisheryData(casaRows: CasaRow[]): ScallopFisheryDataRow[] {
  return casaRows.map((row) => EMPTY_ROW(row.year));
}

/** Map aggregated SAMS + economics lookup to fisheryData format. */
export function mapSamsToFisheryData(
  aggregatedSams: AggregatedSamsRow[],
  economicsByYear: Record<number, YearEconomics>
): ScallopFisheryDataRow[] {
  return aggregatedSams.map((row) => {
    const yearEcon = economicsByYear[row.year] || {};
    const exVessel = yearEcon.total_ex_vessel ?? 0;
    return {
      ...EMPTY_ROW(row.year),
      mean_ex_vessel: exVessel,
      landed_economic_impact: exVessel * 4.6,
      meals_produced: yearEcon.total_meals ?? 0,
      total_jobs_mean: yearEcon.total_jobs ?? 0,
      harvesting_jobs_mean: yearEcon.harvesting_jobs ?? 0,
      processing_jobs_mean: yearEcon.processing_jobs ?? 0,
      distribution_jobs_mean: yearEcon.distribution_jobs ?? 0,
      support_services_jobs_mean: yearEcon.support_services_jobs ?? 0,
      induced_jobs_mean: yearEcon.induced_jobs ?? 0,
    };
  });
}

/** Aggregate SAMS summary rows by year, optionally filtering by areas. */
export function aggregateSamsByYear(
  samsData: Array<{ year?: number; sams_area?: string; total_abundance?: number; total_biomass_mt?: number; exploitable_biomass_mt?: number }>,
  areasToInclude: string[] = ['all']
): AggregatedSamsRow[] {
  if (areasToInclude.length === 0) areasToInclude = ['all'];
  const byYear: Record<number, AggregatedSamsRow> = {};
  samsData.forEach((row) => {
    const rowArea = row.sams_area;
    if (!areasToInclude.includes('all') && rowArea && !areasToInclude.includes(rowArea)) return;
    const year = row.year ?? 0;
    if (!byYear[year]) {
      byYear[year] = { year, total_abundance: 0, total_biomass_mt: 0, exploitable_biomass_mt: 0 };
    }
    byYear[year].total_abundance = (byYear[year].total_abundance ?? 0) + (row.total_abundance ?? 0);
    byYear[year].total_biomass_mt = (byYear[year].total_biomass_mt ?? 0) + (row.total_biomass_mt ?? 0);
    byYear[year].exploitable_biomass_mt = (byYear[year].exploitable_biomass_mt ?? 0) + (row.exploitable_biomass_mt ?? 0);
  });
  return Object.values(byYear).sort((a, b) => a.year - b.year);
}

/** Build economics-by-year from ex-vessel, jobs, and meals API arrays. */
export function buildEconomicsByYear(
  exVesselData: Array<Record<string, unknown>>,
  jobsData: Array<Record<string, unknown>>,
  mealsData: Array<Record<string, unknown>>
): Record<number, YearEconomics> {
  const byYear: Record<number, YearEconomics> = {};
  const addToYear = (
    items: Array<Record<string, unknown>>,
    fields: Array<{ key: keyof YearEconomics; src: string }>
  ) => {
    items.forEach((item) => {
      const year = Number(item.year) ?? 0;
      if (!byYear[year]) byYear[year] = {} as YearEconomics;
      fields.forEach(({ key, src }) => {
        const val = Number(item[src]) || 0;
        (byYear[year] as Record<string, number>)[key as string] =
          ((byYear[year] as Record<string, number>)[key as string] ?? 0) + val;
      });
    });
  };
  addToYear(exVesselData, [{ key: 'total_ex_vessel', src: 'ex_vessel_value' }]);
  addToYear(jobsData, [
    { key: 'total_jobs', src: 'total_jobs' },
    { key: 'harvesting_jobs', src: 'harvesting_jobs' },
    { key: 'processing_jobs', src: 'processing_jobs' },
    { key: 'distribution_jobs', src: 'distribution_jobs' },
    { key: 'support_services_jobs', src: 'support_services_jobs' },
    { key: 'induced_jobs', src: 'induced_jobs' },
  ]);
  addToYear(mealsData, [{ key: 'total_meals', src: 'total_meals' }]);
  return byYear;
}
